<script setup lang="ts">
import { ConfirmationPopup } from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useDeleteAdvertiserRequest, useDuplicateAdvertiser, useGetAdvertiserRequest } from '@/api'
import ArrowIcon from '@/assets/icons/arrow.svg?component'
import CopyIcon from '@/assets/icons/copy.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import StatsIcon from '@/assets/icons/graph.svg?component'
import MoreIcon from '@/assets/icons/more.svg?component'
import PermissionsIcon from '@/assets/icons/settings.svg?component'
import AdvertiserBlocklist from '@/components/Advertiser/AdvertiserBlocklist.vue'
import AdvertiserDetailsAdditionalPayments from '@/components/Advertiser/AdvertiserDetailsAdditionalPayments.vue'
import AdvertiserDetailsCampaigns from '@/components/Advertiser/AdvertiserDetailsCampaigns.vue'
import AdvertiserDetailsInvoices from '@/components/Advertiser/AdvertiserDetailsInvoices.vue'
import AdvertiserDetailsUrlMatchingRules from '@/components/Advertiser/AdvertiserDetailsUrlMatchingRules.vue'
import AdvertiserHeader from '@/components/Advertiser/AdvertiserHeader.vue'
import AdvertiserReports from '@/components/Advertiser/AdvertiserReports.vue'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { FLAG_ADVERTISER_REPORT } from '@/lib/constants/flags.constants'
import { config, redirectAndLoginAdvertiserUrl } from '@/lib/helpers'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TabsComponent, TypographyComponent } from '@/ui-library'

const route = useRoute()
const router = useRouter()
const advertiserId = computed(() => Number(route.params.id as unknown as string))
const { data, isLoading, isFetching, error, getAdvertiser } = useGetAdvertiserRequest(advertiserId)
const { deleteAdvertiser: deleteAdvertiserRequest, error: deleteError, isLoading: isDeletingLoading } = useDeleteAdvertiserRequest()
const { duplicateAdvertiser: duplicateAdvertiserRequest, error: duplicateError, isLoading: isDuplicatingLoading } = useDuplicateAdvertiser()
const isDeletePopupOpen = ref(false)
const isDuplicatePopupOpen = ref(false)
const isAdvertiserReportEnabled = useFlag(FLAG_ADVERTISER_REPORT)
const tabs = computed(() => [
  { label: 'Campaigns', path: 'campaigns' },
  { label: 'Additional payments', path: 'additional-payments' },
  { label: 'Invoices', path: 'invoices' },
  { label: 'Block list', path: 'block-list', tooltipSlotName: 'blocklist-tooltip' },
  { label: 'URL/keywords', path: 'url-matching' },
  ...(isAdvertiserReportEnabled.value ? [{ label: 'Reports', path: 'reports' }] : []),
])
const advertiserGroup = computed(() => data.value?.advertiserGroups?.[0])

const openStatisticsPage = () => {
  if (data.value) {
    window.open(redirectAndLoginAdvertiserUrl(data.value.id), '_blank')
  }
}
const deleteAdvertiser = async () => {
  if (data.value) {
    await deleteAdvertiserRequest(data.value.id)
    isDeletePopupOpen.value = false
    if (!deleteError.value) {
      window.location.href = `${config.legacyAdminAppUrl}/#/stores`
    }
  }
}
const duplicateAdvertiser = async () => {
  if (data.value) {
    const { id } = await duplicateAdvertiserRequest(data.value.id)
    isDuplicatePopupOpen.value = false
    if (!duplicateError.value) {
      return router.push(`/advertisers/${id}`)
    }
  }
}
</script>

<template>
  <HeaderComponent :title="data?.name">
    <template #backButton>
      <ButtonComponent :is-icon="true" :href="`${config.legacyAdminAppUrl}/#/stores`">
        <el-icon color="var(--inverted)" class="icon">
          <ArrowIcon />
        </el-icon>
      </ButtonComponent>
    </template>
    <FlexComponent :gap="8">
      <ButtonComponent variant="transparent" @click="openStatisticsPage">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <StatsIcon />
          </el-icon>
        </template>
        Statistics
      </ButtonComponent>
      <ButtonComponent variant="transparent" :href="`${config.legacyAdminAppUrl}/#/stores/${advertiserId}/users`">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <PermissionsIcon />
          </el-icon>
        </template>
        Permissions
      </ButtonComponent>
      <ButtonComponent variant="transparent" :href="`${config.legacyAdminAppUrl}/#/stores/${advertiserId}/edit`">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <EditIcon />
          </el-icon>
        </template>
        Edit
      </ButtonComponent>
      <el-dropdown trigger="click" :teleported="false" placement="bottom-start">
        <ButtonComponent variant="transparent" is-icon>
          <el-icon :size="24" class="icon" color="var(--inverted)">
            <MoreIcon />
          </el-icon>
        </ButtonComponent>
        <template #dropdown>
          <BoxComponent position="relative" :mt="8" :mb="8">
            <el-dropdown-item @click="isDuplicatePopupOpen = true">
              <FlexComponent :gap="8" align="center">
                <el-icon :size="24" class="icon" color="var(--primary)">
                  <CopyIcon />
                </el-icon>
                <TypographyComponent variant="body-large"> Copy </TypographyComponent>
              </FlexComponent>
            </el-dropdown-item>
            <el-dropdown-item @click="isDeletePopupOpen = true">
              <FlexComponent :gap="8" align="center">
                <el-icon :size="24" class="icon" color="var(--error)">
                  <DeleteIcon />
                </el-icon>
                <TypographyComponent variant="body-large" color="var(--error)"> Delete </TypographyComponent>
              </FlexComponent>
            </el-dropdown-item>
          </BoxComponent>
        </template>
      </el-dropdown>
    </FlexComponent>
  </HeaderComponent>
  <LoadingComponent v-if="isFetching" :size="64" :is-full-size="true" />
  <template v-else-if="!error && data">
    <BoxComponent :p="32" class="container">
      <BoxComponent :mb="32">
        <AdvertiserHeader :advertiser="data" />
      </BoxComponent>
      <BoxComponent :mb="32">
        <TabsComponent :tabs="tabs" :has-dynamic-urls="true">
          <template #tab1>
            <AdvertiserDetailsCampaigns
              :advertiser-group-id="advertiserGroup?.id"
              :advertiser-id="advertiserId"
              :advertiser-name="data.name"
            />
          </template>
          <template #tab2>
            <AdvertiserDetailsAdditionalPayments
              :advertiser-id="data.id"
              :additional-payments="data.additionalPayments"
              :currency="data.currency"
              :legacy-admin-app-url="config.legacyAdminAppUrl"
              :is-loading="isLoading"
              @reload="getAdvertiser"
            />
          </template>
          <template #tab3>
            <AdvertiserDetailsInvoices :advertiser-id="data.id" />
          </template>
          <template #tab4>
            <AdvertiserBlocklist :advertiser-group-id="advertiserGroup?.id" :advertiser-id="advertiserId" />
          </template>
          <template #tab5>
            <AdvertiserDetailsUrlMatchingRules :advertiser-id="advertiserId" />
          </template>
          <template v-if="isAdvertiserReportEnabled" #tab6>
            <AdvertiserReports :store-id="advertiserId" :default-language="data.language" />
          </template>
          <template #blocklist-tooltip>
            <BoxComponent :mb="4">
              <TypographyComponent variant="title-medium">Block list</TypographyComponent>
            </BoxComponent>
            <TypographyComponent variant="body-medium">
              Blocked creators won’t see the advertiser in the Creator App, can’t convert links, and can’t participate in campaigns, even if
              invited. But existing links will continue working.
            </TypographyComponent>
          </template>
        </TabsComponent>
      </BoxComponent>
    </BoxComponent>
  </template>
  <ConfirmationPopup
    v-model="isDuplicatePopupOpen"
    :is-loading="isDuplicatingLoading"
    title="Copy advertiser"
    description="Are you sure you want to copy this advertiser?"
    submit-btn-text="Copy"
    submit-btn-variant="secondary"
    @on-submit="duplicateAdvertiser"
  />
  <ConfirmationPopup v-model="isDeletePopupOpen" :is-loading="isDeletingLoading" title="Delete advertiser" @on-submit="deleteAdvertiser">
    <template #default>
      <BoxComponent :mt="16" :mb="24">
        <TypographyComponent variant="body-large">
          Are you sure you want to permanently delete:
          <TypographyComponent is="span" variant="body-large" :font-weight="600">{{ data?.name }}</TypographyComponent>
          ?
        </TypographyComponent>
      </BoxComponent>
    </template>
  </ConfirmationPopup>
</template>
<style scoped lang="scss">
:deep(.icon) {
  path {
    fill: currentColor;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
